import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Grid,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { apiClient } from '../../services/api/authApi';

interface CalculationResult {
  shipmentId: string;
  totalCO2: number;
  tot?: number;
  ops?: number;
  ene?: number;
  ei?: number;
  tkm?: number;
  transportMode?: string;
  distance?: number;
  calculatedAt: string;
  legs?: {
    legNumber: number;
    co2: number;
    tot?: number;
    ops?: number;
    ene?: number;
    ei?: number;
    tkm?: number;
    distance: number;
    transportMode: string;
    from?: string;
    to?: string;
  }[];
  rawResponse?: any;
}

interface CalculationSummary {
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  errors: any[];
}

interface CalculationResultsDialogProps {
  open: boolean;
  onClose: () => void;
  fileId?: string;
  fileName?: string;
  results?: CalculationSummary;
}

const MetricHeaderCells: React.FC = () => (
  <>
    <TableCell align="right">Distance</TableCell>
    <TableCell align="right">CO₂</TableCell>
    <TableCell align="right">TOT</TableCell>
    <TableCell align="right">OPS</TableCell>
    <TableCell align="right">ENE</TableCell>
    <TableCell align="right">TKM</TableCell>
    <TableCell align="right">EI</TableCell>
  </>
);

const SummaryStat: React.FC<{
  label: string;
  value: React.ReactNode;
  md: number;
  valueColor?: string;
}> = ({ label, value, md, valueColor }) => (
  <Grid item xs={6} sm={4} md={md}>
    <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
      {label}
    </Typography>
    <Typography variant="h6" color={valueColor}>
      {value}
    </Typography>
  </Grid>
);

export const CalculationResultsDialog: React.FC<CalculationResultsDialogProps> = ({
  open,
  onClose,
  fileId,
  fileName,
  results: initialSummary,
}) => {
  const [results, setResults] = useState<CalculationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [summary, setSummary] = useState({
    totalShipments: 0,
    successfulRows: 0,
    failedRows: 0,
    totalCO2: 0,
    totalTOT: 0,
    totalOPS: 0,
    totalENE: 0,
    totalDistance: 0,
    totalTKM: 0,
    averageCO2: 0,
    averageEI: 0,
  });

  useEffect(() => {
    if (open) {
      if (initialSummary) {
        setSummary({
          totalShipments: initialSummary.totalRows,
          successfulRows: initialSummary.successfulRows,
          failedRows: initialSummary.failedRows,
          totalCO2: 0,
          totalTOT: 0,
          totalOPS: 0,
          totalENE: 0,
          totalDistance: 0,
          totalTKM: 0,
          averageCO2: 0,
          averageEI: 0,
        });
        setResults([]);
      } else if (fileId) {
        fetchResults();
      }
    }
  }, [open, fileId, initialSummary]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/xml/calc/results/${fileId}`);

      if (response.data.success && response.data.data) {
        const resultsData = response.data.data.results || [];
        setResults(resultsData);

        // Log for debugging
        console.log('Results data:', resultsData);
        if (resultsData.length > 0 && resultsData[0].rawResponse) {
          console.log('Raw API response:', resultsData[0].rawResponse);
        }

        // Calculate summary statistics
        const totalCO2 = resultsData.reduce((sum: number, r: CalculationResult) => {
          const co2Value = Number(r.totalCO2) || 0;
          return sum + co2Value;
        }, 0);
        const totalTOT = resultsData.reduce((sum: number, r: CalculationResult) => sum + (Number(r.tot) || 0), 0);
        const totalOPS = resultsData.reduce((sum: number, r: CalculationResult) => sum + (Number(r.ops) || 0), 0);
        const totalENE = resultsData.reduce((sum: number, r: CalculationResult) => sum + (Number(r.ene) || 0), 0);
        const totalDistance = resultsData.reduce((sum: number, r: CalculationResult) => sum + (Number(r.distance) || 0), 0);
        const totalTKM = resultsData.reduce((sum: number, r: CalculationResult) => sum + (Number(r.tkm) || 0), 0);
        const totalEI = resultsData.reduce((sum: number, r: CalculationResult) => sum + (Number(r.ei) || 0), 0);

        const successCount = resultsData.filter((r: CalculationResult) => !(r.rawResponse?.error || r.rawResponse?.isError)).length;
        const failedCount = resultsData.length - successCount;

        setSummary({
          totalShipments: resultsData.length,
          successfulRows: successCount,
          failedRows: failedCount,
          totalCO2: totalCO2,
          totalTOT: totalTOT,
          totalOPS: totalOPS,
          totalENE: totalENE,
          totalDistance: totalDistance,
          totalTKM: totalTKM,
          averageCO2: resultsData.length > 0 ? totalCO2 / resultsData.length : 0,
          averageEI: resultsData.length > 0 ? totalEI / resultsData.length : 0,
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch results:', err);
      setError('Failed to load calculation results');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResults = async () => {
    if (!fileId) return;
    try {
      const response = await apiClient.get(`/excel/download/${fileId}`, {
        responseType: 'blob',
      });

      const url = globalThis.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `results_${fileId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
      setError('Failed to download results file');
    }
  };

  const toggleRowExpanded = (shipmentId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(shipmentId)) {
      newExpanded.delete(shipmentId);
    } else {
      newExpanded.add(shipmentId);
    }
    setExpandedRows(newExpanded);
  };

  const formatCO2 = (value: number | undefined | null): string => {
    if (value == null || Number.isNaN(value)) return '0.00 kg';
    return Number(value).toFixed(2) + ' kg';
  };

  const formatDistance = (value: number | undefined | null): string => {
    if (value == null || Number.isNaN(value)) return '0.00 km';
    return Number(value).toFixed(2) + ' km';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Calculation Results {fileName && `- ${fileName}`}
          </Typography>
          <IconButton onClick={handleDownloadResults} color="primary">
            <DownloadIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Show errors if any results have errors */}
            {results.some(r => r.rawResponse?.error || r.rawResponse?.isError) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Some calculations encountered errors:
                </Typography>
                {results.filter(r => r.rawResponse?.error || r.rawResponse?.isError).map((r, idx) => (
                  <Typography key={r.shipmentId} variant="body2" sx={{ ml: 2 }}>
                    • <strong>{r.shipmentId}</strong>: {r.rawResponse.error || 'Calculation failed'}
                  </Typography>
                ))}
              </Alert>
            )}

            {/* Summary Section */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
              <Grid container spacing={2}>
                <SummaryStat label="Total" value={summary.totalShipments} md={2} />
                <SummaryStat label="Successful" value={summary.successfulRows} md={2} />
                <SummaryStat
                  label="Failed"
                  value={summary.failedRows}
                  md={2}
                  valueColor={summary.failedRows > 0 ? "error.light" : "inherit"}
                />
                <SummaryStat label="Total CO₂" value={formatCO2(summary.totalCO2)} md={3} />
                <SummaryStat label="Total Distance" value={formatDistance(summary.totalDistance)} md={3} />
                <SummaryStat label="Avg CO₂/Shipment" value={formatCO2(summary.averageCO2)} md={3} />
                {summary.totalTOT > 0 && (
                  <SummaryStat label="Total Emissions (TOT)" value={formatCO2(summary.totalTOT)} md={3} />
                )}
                {summary.totalOPS > 0 && (
                  <SummaryStat label="Operations (OPS)" value={formatCO2(summary.totalOPS)} md={3} />
                )}
                {summary.totalENE > 0 && (
                  <SummaryStat label="Energy (ENE)" value={formatCO2(summary.totalENE)} md={3} />
                )}
                {summary.totalTKM > 0 && (
                  <SummaryStat label="Tonne-km (TKM)" value={summary.totalTKM.toFixed(0)} md={3} />
                )}
              </Grid>
            </Paper>

            {/* Results Table */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40}></TableCell>
                    <TableCell>Shipment ID</TableCell>
                    <TableCell>Mode</TableCell>
                    <MetricHeaderCells />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="textSecondary" py={2}>
                          No calculation results available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    results.map((result) => (
                      <React.Fragment key={result.shipmentId}>
                        <TableRow
                          hover
                          sx={{
                            bgcolor: (result.rawResponse?.error || result.rawResponse?.isError) ? 'error.50' : 'inherit'
                          }}
                        >
                          <TableCell>
                            {result.legs && result.legs.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpanded(result.shipmentId)}
                              >
                                {expandedRows.has(result.shipmentId) ?
                                  <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {result.shipmentId}
                            </Typography>
                            {(result.rawResponse?.error || result.rawResponse?.isError) && (
                              <Typography variant="caption" color="error">
                                {result.rawResponse.error}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={result.transportMode || 'Mixed'}
                              size="small"
                              color={result.transportMode === 'error' ? 'error' : 'primary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatDistance(result.distance)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={(() => {
                                if (result.rawResponse?.error || result.rawResponse?.isError) return 'error';
                                if (Number(result.totalCO2) > 1000) return 'error';
                                return 'success';
                              })()}
                            >
                              {formatCO2(result.totalCO2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {result.tot ? formatCO2(result.tot) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {result.ops ? formatCO2(result.ops) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {result.ene ? formatCO2(result.ene) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {result.tkm ? result.tkm.toFixed(0) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {result.ei ? result.ei.toFixed(3) : '-'}
                          </TableCell>
                        </TableRow>

                        {/* Expandable details row */}
                        {result.legs && result.legs.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={10} sx={{ py: 0 }}>
                              <Collapse in={expandedRows.has(result.shipmentId)}>
                                <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="subtitle1" gutterBottom>
                                    Leg Details:
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Leg #</TableCell>
                                        <TableCell>Mode</TableCell>
                                        <TableCell>From</TableCell>
                                        <TableCell>To</TableCell>
                                        <MetricHeaderCells />
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {result.legs.map((leg: any, index: number) => (
                                        <TableRow key={`${result.shipmentId}-leg-${leg.legNumber ?? index + 1}`}>
                                          <TableCell>{leg.legNumber || index + 1}</TableCell>
                                          <TableCell>{leg.transportMode}</TableCell>
                                          <TableCell>{leg.from || '-'}</TableCell>
                                          <TableCell>{leg.to || '-'}</TableCell>
                                          <TableCell align="right">
                                            {formatDistance(leg.distance)}
                                          </TableCell>
                                          <TableCell align="right">
                                            {formatCO2(leg.co2)}
                                          </TableCell>
                                          <TableCell align="right">
                                            {leg.tot ? formatCO2(leg.tot) : '-'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {leg.ops ? formatCO2(leg.ops) : '-'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {leg.ene ? formatCO2(leg.ene) : '-'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {leg.tkm ? leg.tkm.toFixed(0) : '-'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {leg.ei ? leg.ei.toFixed(3) : '-'}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadResults}
          disabled={loading || results.length === 0}
        >
          Download Results
        </Button>
      </DialogActions>
    </Dialog>
  );
};